import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';
import { env } from '@/lib/env';

/**
 * Saves a workflow's bundle (service spec 162).
 *
 * A scripted fetch for the reason `organization-admin/csvDownload.ts` gives: the
 * body is a file, and a plain link cannot carry the bearer token. Renews once on
 * a 401 and replays. A refusal carries the service's own sentence (a draft
 * child, an agent that is gone), which is what the dialog shows.
 */
function send(url: string): Promise<Response> {
  const token = getAccessToken();
  return fetch(url, {
    headers: token
      ? { Accept: 'application/zip', Authorization: `Bearer ${token}` }
      : { Accept: 'application/zip' },
  });
}

export function bundlePath(
  workspaceId: string,
  workflowId: string,
  { agents, skills }: { agents: string[]; skills: string[] },
): string {
  const query = new URLSearchParams();
  for (const agent of agents) query.append('agent', agent);
  for (const skill of skills) query.append('skill', skill);
  const tail = query.size > 0 ? `?${query.toString()}` : '';
  return `/api/workspaces/${workspaceId}/workflows/${workflowId}/bundle${tail}`;
}

/** The bundle's bytes, for a copy that never touches the person's disk (spec 205). */
export async function fetchBundle(path: string): Promise<Blob> {
  const url = `${env.VITE_API_BASE_URL}${path}`;
  let response = await send(url);
  if (response.status === 401 && getAccessToken()) {
    const renewed = await renewAccessToken();
    if (renewed) response = await send(url);
  }
  if (!response.ok) throw new Error(await refusal(response));
  return response.blob();
}

export async function downloadBundle(
  path: string,
  name: string,
): Promise<void> {
  const objectUrl = URL.createObjectURL(await fetchBundle(path));
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = name;
    anchor.click();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function refusal(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof body.message === 'string'
    ) {
      return body.message;
    }
  } catch {
    // Not JSON: fall through to the plain sentence.
  }
  return 'The bundle could not be downloaded.';
}
